import React, {
    createContext,
    useContext,
    ReactNode,
    useState,
    useMemo,
    useEffect,
} from 'react';
import {
    Planogram,
    PlanogramData,
    useGetPlanograms,
} from '@/store/Planogram/findAll';
import { UpdatePlanogramParams } from '@/store/Planogram/update';
import { checkIfExistLineChanged } from '../rules/checkIfExistLineChanged';

export type PlanogramUI = Planogram & {
    deleted: boolean;
    edited: boolean;
    key: number;
    isDirthy?: boolean;
    copy?: PlanogramUI;
    price_final_in_cents_after?: number;
    price_list_in_cents_after?: number;
};

type FilterPlanogram = {
    page: number;
    perPage: number;
    'filters[stores][]': number[];
    'filters[categories][]': number[];
    'filters[product]': string;
    'filters[barCode]': string;
};

type PlanogramAutomationContextType = {
    isLoadingPlanogram: boolean;
    isFetching: boolean;
    refetch: () => void;
    params: FilterPlanogram;
    setParams: React.Dispatch<React.SetStateAction<FilterPlanogram>>;
    planogramList: PlanogramData | undefined;
    planograms: PlanogramUI[];
    setPlanogramsList: React.Dispatch<React.SetStateAction<PlanogramUI[]>>;
    planogramsCopy: PlanogramUI[];
    setCopyPlanograms: React.Dispatch<React.SetStateAction<PlanogramUI[]>>;
    planogramChecked: PlanogramUI[];
    setPlanogramsChecked: React.Dispatch<React.SetStateAction<PlanogramUI[]>>;
    currentStep: number;
    setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
    planogramsUpdated: UpdatePlanogramParams[];
    setPlanogramsUpdated: React.Dispatch<
        React.SetStateAction<UpdatePlanogramParams[]>
    >;
};

export enum PlanogramAutomationSteps {
    products = 0,
    update = 1,
    confirm = 2,
}

const PlanogramAutomationContext =
    createContext<PlanogramAutomationContextType | null>(null);

type ProviderProps = {
    children: ReactNode;
    initialPlanograms?: Planogram[];
    initialStep?: PlanogramAutomationSteps;
    fetchPlanograms?: boolean;
};

export function PlanogramAutomationProvider({
    children,
    initialPlanograms = [],
    initialStep = PlanogramAutomationSteps.products,
    fetchPlanograms = true,
}: ProviderProps) {
    const initialPlanogramState = useMemo(
        () =>
            initialPlanograms.map((item) => ({
                ...item,
                deleted: false,
                edited: false,
                key: item.id,
            })),
        [initialPlanograms],
    );
    const [planograms, setPlanogramsList] = useState<PlanogramUI[]>(
        initialPlanogramState,
    );
    const [planogramsCopy, setCopyPlanograms] = useState<PlanogramUI[]>(
        initialPlanogramState.map((item) => ({ ...item })),
    );
    const [planogramChecked, setPlanogramsChecked] = useState<PlanogramUI[]>(
        initialPlanogramState,
    );
    const [currentStep, setCurrentStep] = useState(initialStep);
    const [planogramsUpdated, setPlanogramsUpdated] = useState<
        UpdatePlanogramParams[]
    >([]);

    const [params, setParams] = useState({
        page: 1,
        perPage: 10,
        'filters[stores][]': [],
        'filters[categories][]': [],
        'filters[product]': '',
        'filters[barCode]': '',
    });

    const {
        data: planogramList,
        isLoading: isLoadingPlanogram,
        refetch,
        isFetching,
    } = useGetPlanograms(params, fetchPlanograms);

    /**
     *
     * @param planogramsToChange
     * @param planogramEdited
     * @returns
     */

    const values = useMemo(
        () => ({
            isLoadingPlanogram,
            planogramList,
            refetch,
            isFetching,
            setParams,
            params,
            planogramsCopy,
            setCopyPlanograms,
            planogramChecked,
            setPlanogramsChecked,
            setPlanogramsList,
            planograms,
            currentStep,
            setCurrentStep,
            planogramsUpdated,
            setPlanogramsUpdated,
        }),
        [
            isLoadingPlanogram,
            planogramList,
            refetch,
            isFetching,
            setParams,
            params,
            planogramsCopy,
            setCopyPlanograms,
            planogramChecked,
            setPlanogramsChecked,
            setPlanogramsList,
            currentStep,
            setCurrentStep,
            planograms,
            planogramsUpdated,
            setPlanogramsUpdated,
        ],
    );

    useEffect(() => {
        if (fetchPlanograms && planogramList) {
            const addKeyInPlanograms = planogramList.data.map((item) => ({
                ...item,
                deleted: false,
                edited: false,
                key: item.id,
            }));
            setPlanogramsList(addKeyInPlanograms);
            const checkingChangedData = checkIfExistLineChanged(
                addKeyInPlanograms,
                planogramChecked,
            );
            setCopyPlanograms(checkingChangedData);
        }
    }, [planogramList?.data, isFetching, fetchPlanograms]);

    return (
        <PlanogramAutomationContext.Provider value={values}>
            {children}
        </PlanogramAutomationContext.Provider>
    );
}

export function usePlanogramAutomationContext() {
    const context = useContext(PlanogramAutomationContext);

    if (!context) {
        throw new Error(
            'usePlanogramAutomationContext must be used within a PlanogramAutomationProvider',
        );
    }

    return context;
}
